/**
 * @fileoverview The instructor's Lessons tab inside a program (I9).
 *
 * Every lesson this instructor taught under the program, and — the reason to
 * open the tab — every past session of their classes here that has no notes,
 * as rows of their own, with the lesson typed ahead of next week's session
 * marked Planned rather than counted. The same filter bar the org uses
 * (F4), so "missing notes for the Tuesday class" is the same question on
 * both sides and gets the same answer; the counts here and the class tile's
 * "Need notes" come from the same sessions read.
 *
 * Sessions that need no notes can be ticked and cleared, or asked about when
 * the organization must agree (useInstructorNotesClear).
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { composeHrefForOccurrence } from "@/lib/schedule";
import { fetchLessonHistory, filterLessons, lessonStatus, missingRow, sortChronologically } from "@/lib/lessonHistory";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import { Button, Card, ListRow, SectionHeader, Tag } from "@/components/studio/primitives";
import useInstructorNotesClear from "@/components/studio/useInstructorNotesClear";
import { clearanceLine, clearanceTag } from "@/lib/notesClearance";

function shortDate(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function ProgramLessonsTab({ programId, classes = [], filter, onFilter }) {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState(null);
  const [sessions, setSessions] = useState({});
  const [failed, setFailed] = useState(false);
  const [bar, setBar] = useState({ period: "all", classId: null, search: "" });
  // Bumped after a clear, to re-read the sessions it changed.
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLessons(null);
    setFailed(false);
    fetchLessonHistory(api, { signal: controller.signal })
      .then((all) => {
        if (!controller.signal.aborted) setLessons(all.filter((l) => l.program_id === programId));
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [programId]);

  // The sessions of each class taught here: what has no notes yet, and what
  // was cancelled. The same read the class page and its tile use.
  useEffect(() => {
    let cancelled = false;
    const ids = classes.map((c) => c.class_id);
    Promise.all(
      ids.map((id) =>
        api
          .get(`/classes/${id}/sessions`)
          .then((res) => [id, res.data?.sessions || []])
          .catch(() => [id, []]),
      ),
    ).then((pairs) => {
      if (!cancelled) setSessions(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [classes, revision]);

  const classNames = useMemo(() => Object.fromEntries(classes.map((c) => [c.class_id, c.name])), [classes]);

  const rows = useMemo(() => {
    const extra = [];
    for (const cls of classes) {
      for (const s of sessions[cls.class_id] || []) {
        // A session a substitute covers for someone else owes the reader
        // nothing; its lesson, once written, arrives from the history below.
        if (s.upcoming || s.notes || (s.covered && !s.covered.mine)) continue;
        extra.push(
          missingRow(
            { ...s, class_id: cls.class_id },
            { classId: cls.class_id, className: cls.name, status: s.status === "cancelled" ? "cancelled" : undefined },
          ),
        );
      }
    }
    return [...(lessons || []), ...extra];
  }, [lessons, sessions, classes]);

  // The class's name, or nothing: a lesson whose class is no longer one of
  // this instructor's here has no second line rather than its title twice.
  const nameFor = (l) => classNames[l.class_id] || "";
  const status = filter || "all";
  const filtered = useMemo(
    () => sortChronologically(filterLessons(rows, { ...bar, kind: "all", status }, nameFor)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, bar, status, classNames],
  );
  const missing = rows.filter((r) => r.missing).length;
  const clearing = useInstructorNotesClear({
    rows: filtered,
    onChanged: () => setRevision((n) => n + 1),
    testId: "program-notes-clear",
  });

  if (failed) {
    return (
      <Card data-testid="program-lessons-failed">
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load your lessons in this program.</p>
      </Card>
    );
  }
  if (!lessons) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>;

  return (
    <div data-testid="program-lessons">
      <SectionHeader>
        Lessons ({filtered.length})
        {missing > 0 && (
          <span className="ml-3 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]" data-testid="program-lessons-missing">
            {missing} {missing === 1 ? "session needs" : "sessions need"} notes
          </span>
        )}
      </SectionHeader>
      <LessonFilterBar
        value={{ ...bar, status }}
        onChange={(next) => {
          if (next.status !== status) onFilter?.(next.status === "all" ? null : next.status);
          setBar({ period: next.period, classId: next.classId, search: next.search });
        }}
        classes={classes.map((c) => ({ id: c.class_id, name: c.name }))}
        searchPlaceholder="Search lessons"
      />
      <Card padding={0}>
        {filtered.length > 0 && clearing.toolbar}
        {filtered.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="program-lessons-empty">
            {rows.length === 0 ? "Nothing taught under this program yet." : "Nothing matches these filters."}
          </div>
        ) : (
          filtered.map((l, i) => {
            const st = lessonStatus(l);
            const day = l.scheduled_date || (l.created_at || "").slice(0, 10);
            // A substitute's lesson in one of the reader's classes: greyed, read-only.
            const substitute = Boolean(l.substitute) && l.read_only !== false;
            const gap = st === "missing" || st === "cancelled" || st === "cleared";
            const asked = st === "missing" ? clearanceTag(l.notes_clearance) : null;
            return (
              <div key={l.lesson_id} className={substitute ? "opacity-60" : undefined}>
              <ListRow
                borderBottom={i < filtered.length - 1}
                data-testid={`program-lesson-${l.lesson_id}`}
                leading={st === "missing" || st === "cleared" ? clearing.checkbox(l) : undefined}
                title={gap ? shortDate(day) : l.title || nameFor(l) || "Lesson"}
                subtitle={
                  st === "missing" || st === "cleared"
                    ? [nameFor(l), l.time_local, clearanceLine(l.notes_clearance)].filter(Boolean).join(" · ")
                    : st === "cancelled"
                      ? [nameFor(l), "Cancelled"].filter(Boolean).join(" · ")
                      : [nameFor(l), substitute ? `Taught by ${l.substitute.name || "a substitute"}` : null].filter(Boolean).join(" · ") ||
                        undefined
                }
                meta={gap ? undefined : when(l.created_at)}
                trailing={
                  st === "missing" ? (
                    <span className="flex items-center gap-2">
                      {asked && <Tag accent>{asked.label}</Tag>}
                      <Button
                        onClick={(e) => {
                          e?.stopPropagation?.();
                          navigate(composeHrefForOccurrence({ ...l, class_id: l.class_id }));
                        }}
                        data-testid={`write-notes-${l.occurrence_id}`}
                      >
                        Write notes
                      </Button>
                    </span>
                  ) : st === "cleared" ? (
                    <Tag>Notes cleared</Tag>
                  ) : st === "cancelled" ? (
                    <Tag>No session</Tag>
                  ) : (
                    <span className="flex items-center gap-2">
                      {substitute && <Tag>Substitute</Tag>}
                      {st === "planned" && <Tag>Planned</Tag>}
                      {l.manual && !substitute && <Tag>Typed</Tag>}
                      {l.email_sent && <Tag accent>Shared</Tag>}
                    </span>
                  )
                }
                onPress={st === "written" || st === "planned" ? () => navigate(`/lesson/${l.lesson_id}${l.kind === "class" ? "?kind=class" : ""}`) : undefined}
              />
              </div>
            );
          })
        )}
      </Card>
      {clearing.dialogs}
    </div>
  );
}
